import { useEffect, useRef, useState } from 'react';

/**
 * useHeroImagePreloader Hook
 * 
 * Preloads all hero images in the background after the first page load.
 * This ensures hero images are cached and ready when users navigate between pages.
 * 
 * @param {Array<string>} heroImageSources - Array of hero image source URLs to preload
 * @returns {Object} Preload status - { isPreloading: boolean, hasPreloaded: boolean }
 */
const useHeroImagePreloader = (heroImageSources = []) => {
  const preloadInitiatedRef = useRef(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const [hasPreloaded, setHasPreloaded] = useState(false);

  useEffect(() => {
    // Only preload once per session
    if (preloadInitiatedRef.current || !heroImageSources.length) {
      return;
    }

    // Use requestIdleCallback if available, otherwise use setTimeout
    const preloadImages = () => {
      setIsPreloading(true);
      
      let loadedCount = 0;
      const totalImages = heroImageSources.filter(src => src).length;
      
      // Handle edge case where no valid images to preload
      if (totalImages === 0) {
        setIsPreloading(false);
        setHasPreloaded(true);
        preloadInitiatedRef.current = true;
        return;
      }
      
      const checkComplete = () => {
        loadedCount++;
        if (loadedCount >= totalImages) {
          setIsPreloading(false);
          setHasPreloaded(true);
          preloadInitiatedRef.current = true;
        }
      };
      
      heroImageSources.forEach((src) => {
        if (src) {
          const img = new Image();
          img.src = src;
          // Optionally handle load/error events for debugging
          img.onload = () => {
            // Image successfully preloaded
            checkComplete();
          };
          img.onerror = () => {
            // Silently handle errors - image might not exist or failed to load
            checkComplete();
          };
        }
      });
    };

    // Preload after a short delay to not interfere with initial page load
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(preloadImages, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(preloadImages, 1000);
    }
    // Intentionally omit heroImageSources from dependencies - we only want to preload once per session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isPreloading, hasPreloaded };
};

export default useHeroImagePreloader;
